<template>
    <!-- 单个服务器卡片：状态 + 命令列表 -->
    <article class="ssh-card">
        <header class="ssh-card__head">
            <div class="ssh-card__status" :class="machine.portOpen ? 'is-online' : 'is-offline'">
                <el-image class="ssh-card__status-img"
                    :src="require(machine.portOpen ? '../../../assets/switch/computer_open.svg' : '../../../assets/switch/computer_close.svg')"
                    fit="scale-down" />
            </div>
            <div class="ssh-card__title">
                <div class="ssh-card__name" :title="machine.name">{{ machine.name }}</div>
                <div class="ssh-card__addr" :title="machine.address">{{ machine.address }}</div>
            </div>
            <!-- 入口按钮整体成组：宽度不够时整块换到下一行，避免挤占服务器名称 -->
            <div class="ssh-card__ops" v-if="machine.canUseConsole">
                <!-- 控制台入口：服务器归属者或控制台执行权限团队成员可见（canUseConsole 由后端算好下发） -->
                <el-button class="ssh-card__console" size="small" type="primary" plain
                    @click.stop="$emit('console', machine)">
                    <el-icon class="ssh-card__console-icon">
                        <Monitor />
                    </el-icon>
                    控制台
                </el-button>
                <!-- 文件入口：权限与控制台一致（能开 shell 的人本来就能读写任意文件） -->
                <el-button class="ssh-card__console ssh-card__file" size="small" @click.stop="$emit('file', machine)">
                    <el-icon class="ssh-card__console-icon">
                        <FolderOpened />
                    </el-icon>
                    文件
                </el-button>
            </div>
        </header>
        <div class="ssh-card__body">
            <div class="ssh-cmd" v-for="cmd in machine.cmdList" :key="cmd">
                <span class="ssh-cmd__dot" :class="cmdStatusClass(cmd)"></span>
                <span class="ssh-cmd__name">{{ cmd.name }}</span>
                <div class="ssh-cmd__actions" v-if="isLogin">
                    <!-- 生产防护锁：长按解锁按钮 -->
                    <el-tooltip v-if="machine.prodLock" placement="left" popper-class="ssh-lock-tip"
                        :content="cmd._prodUnlock ? '已解锁，点击右侧按钮执行（5 秒后自动上锁）' : '生产防护锁：长按 1 秒解锁'">
                        <div class="lock-btn"
                            :class="{ 'is-holding': cmd._holdProgress > 0, 'is-unlocked': cmd._prodUnlock }"
                            :style="holdRingStyle(cmd)" @mousedown="$emit('hold-start', machine, cmd)"
                            @mouseup="$emit('hold-cancel', cmd)" @mouseleave="$emit('hold-cancel', cmd)"
                            @touchstart.prevent="$emit('hold-start', machine, cmd)"
                            @touchend="$emit('hold-cancel', cmd)" @touchcancel="$emit('hold-cancel', cmd)"
                            @click="handleLockedClick(machine, cmd)">
                            <el-icon :size="15">
                                <Unlock v-if="cmd._prodUnlock" />
                                <Lock v-else />
                            </el-icon>
                        </div>
                    </el-tooltip>
                    <!-- 执行按钮（不做悬浮提示，避免遮挡；点击时用 ElMessage 反馈） -->
                    <el-progress :class="['cust-progress', { 'is-disabled': isLocked(machine, cmd) }]"
                        type="circle" :percentage="cmd._waitProgress"
                        :status="cmd._waitProgress == 100 ? 'success' : 'warning'"
                        :color="cmd._waitProgress == 100 ? 'var(--el-color-success)' : 'var(--el-color-warning)'"
                        :stroke-width="5" :width="34" @click="$emit('run', machine, cmd, $event)"
                        :style="{ cursor: isLocked(machine, cmd) ? 'not-allowed' : (cmd._waitProgress == 100 ? 'pointer' : 'not-allowed') }">
                        <template #default>
                            <span v-if="cmd._waitProgress">
                                <el-icon v-if="cmd._waitProgress == 100" :size="21"
                                    color="var(--el-color-success)">
                                    <CaretRight />
                                </el-icon>
                                <span v-else style="font-size: 12px;">{{ cmd._waitSeconds }}</span>
                            </span>
                            <span v-else style="font-size: 12px;">
                                <el-icon :size="20">
                                    <More />
                                </el-icon>
                            </span>
                        </template>
                    </el-progress>
                </div>
            </div>
        </div>
        <footer class="ssh-card__foot" v-if="machine.description || machine.teamList.length > 0">
            <p class="ssh-card__desc" v-if="machine.description">{{ machine.description }}</p>
            <div class="ssh-card__teams" v-if="machine.teamList.length > 0">
                <el-tag class="ssh-card__team" v-for="team in machine.teamList" :key="team.id" type="success"
                    size="small">{{ team.name }}</el-tag>
            </div>
        </footer>
    </article>
</template>

<script>
import { ElMessage } from 'element-plus';

export default {
    name: 'SshServerCard',
    props: {
        machine: { type: Object, required: true },
        isLogin: { type: Boolean, default: false }
    },
    // 长按解锁/取消、点击执行、打开控制台/文件等交互交给父组件处理（父组件持有定时器与状态）
    emits: ['run', 'hold-start', 'hold-cancel', 'console', 'file'],
    methods: {
        // 命令端口状态：绿=已开放，红=未开放，灰=未配置端口
        cmdStatusClass(cmd) {
            if (!cmd.port) return 'is-none';
            return cmd.portOpen ? 'is-online' : 'is-offline';
        },
        // 生产防护锁：服务器开启后，需长按锁钮解锁才允许执行
        isLocked(machine, cmd) {
            return !!(machine.prodLock && !cmd._prodUnlock);
        },
        // 解锁后：绿色计时环，5 秒内逐渐收掉（露出的红色底环即"已回锁"）
        holdRingStyle(cmd) {
            if (cmd._prodUnlock) {
                const left = cmd._relockProgress === undefined ? 100 : cmd._relockProgress;
                const unlockDeg = left * 3.6;
                return { background: `conic-gradient(#67c23a ${unlockDeg}deg, #f56c6c ${unlockDeg}deg)` };
            }
            // 长按进度：绿色进度环压在红色底环上逐渐填充
            const progress = cmd._holdProgress || 0;
            if (progress <= 0) return {};
            const deg = progress * 3.6;
            return { background: `conic-gradient(#67c23a ${deg}deg, #f56c6c ${deg}deg)` };
        },
        // 未解锁时点击锁钮：给一次提示
        handleLockedClick(machine, cmd) {
            if (this.isLocked(machine, cmd)) {
                ElMessage({ message: '生产防护锁已启用，请长按锁钮 1 秒解锁', type: 'warning', duration: 3 * 1000 });
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.ssh-card {
    /* 多栏布局中列间距用 column-gap，卡片之间的纵向间距只能靠 margin */
    margin: 0 0 16px;
    /* 避免卡片被跨栏截断 */
    break-inside: avoid;
    border: 1px solid var(--ssh-line);
    border-radius: 14px;
    background-color: #ffffff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
    overflow: hidden;
    transition:
        box-shadow 0.25s,
        transform 0.25s,
        border-color 0.25s;
}

.ssh-card:hover {
    border-color: #d8e1ef;
    box-shadow: 0 14px 30px -16px rgba(28, 47, 84, 0.3);
    transform: translateY(-2px);
}

/* 卡片头：状态图标 + 名称地址 + 入口按钮 */
.ssh-card__head {
    display: flex;
    /* 按需换行：宽度不够时让按钮整块落到下一行，而不是把服务器名称挤没 */
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    row-gap: 8px;
    padding: 12px 14px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.ssh-card__status {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background-color: #f2f5fa;
    box-shadow: 0 0 0 1px var(--ssh-line) inset;
}

.ssh-card__status.is-online {
    background-color: #f0f9eb;
    box-shadow: 0 0 0 1px #e1f3d8 inset;
}

.ssh-card__status-img {
    width: 28px;
    height: 18px;
}

.ssh-card__title {
    /* 给一个 flex-basis（而不是 auto）：空间不足时先触发按钮换行，而不是把名称压到几像素 */
    flex: 1 1 160px;
    min-width: 0;
}

/* 名称必须完整可见：允许折行，不再用省略号截断 */
.ssh-card__name {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
    color: var(--ssh-text);
    overflow-wrap: anywhere;
}

.ssh-card__addr {
    margin-top: 2px;
    font-size: 12px;
    color: #aab4c4;
    overflow-wrap: anywhere;
}

/* 入口按钮组：换行到第二行时靠右对齐 */
.ssh-card__ops {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
    margin-left: auto;
}

.ssh-card__console {
    flex: 0 0 auto;
    height: 28px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 12px;
}

.ssh-card__console-icon {
    margin-right: 4px;
}

/* 文件按钮用次要样式，避免和「控制台」抢视觉焦点 */
.ssh-card__file {
    color: #606266;
    background-color: #fff;
    border-color: #e4e7ed;
}

.ssh-card__file:hover {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
    border-color: var(--el-color-primary-light-5);
}

/* 卡片体：命令行列表 */
.ssh-card__body {
    padding: 6px;
}

.ssh-cmd {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 8px;
    border-radius: 8px;
    transition: background-color 0.2s;
}

.ssh-cmd:hover {
    background-color: #f6f9ff;
}

/* 状态圆点：绿=端口已开放，红=端口未开放，灰=未配置端口 */
.ssh-cmd__dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #d8dee8;
}

.ssh-cmd__dot.is-online {
    background-color: #67c23a;
    box-shadow: 0 0 0 3px rgba(103, 194, 58, 0.15);
}

.ssh-cmd__dot.is-offline {
    background-color: #f56c6c;
    box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.15);
}

.ssh-cmd__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: var(--ssh-text-sub);
    transition: color 0.2s;
}

.ssh-cmd:hover .ssh-cmd__name {
    color: var(--ssh-text);
}

/* 生产防护锁：解锁按钮与执行按钮并排（靠右对齐） */
.ssh-cmd__actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-end;
}

/* 卡片脚：说明 + 所属团队 */
.ssh-card__foot {
    padding: 10px 14px 12px;
    border-top: 1px solid #eef1f6;
    background-color: #fbfcfe;
}

.ssh-card__desc {
    margin: 0;
    font-size: 12px;
    line-height: 1.5;
    color: var(--ssh-text-weak);
}

.ssh-card__teams {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.ssh-card__team {
    margin: 0;
}

/* ---------- 生产防护锁：长按解锁按钮（环形填充） ---------- */
.lock-btn {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-right: 8px;
    border-radius: 50%;
    /* 默认（锁定）：红色锁 + 红色底环 */
    background: #f56c6c;
    color: #f56c6c;
    cursor: pointer;
    user-select: none;
    transition:
        color 0.15s,
        background-color 0.15s;
}

/* 白色内芯，形成环状底槽（inset 即环的粗细，环厚 = inset，内孔 = 外径 - 2 × inset） */
.lock-btn::before {
    content: '';
    position: absolute;
    inset: 5px;
    border-radius: 50%;
    background: #ffffff;
}

.lock-btn .el-icon {
    position: relative;
    z-index: 1;
}

/* 长按中：绿色进度环由 holdRingStyle 实时绘制，锁图标保持红色（此时仍未解锁） */

/* 已解锁：绿色开锁图标（环的颜色由 holdRingStyle 按倒计时实时绘制） */
.lock-btn.is-unlocked {
    color: #67c23a;
}

/* 执行按钮 */
.cust-progress {
    flex: 0 0 auto;
}

.cust-progress :deep(.el-progress__text) {
    min-width: 0;
}

/* 未解锁时执行按钮置灰 */
.cust-progress.is-disabled {
    filter: grayscale(1);
    opacity: 0.5;
}

/* ---------- 移动端适配 ---------- */
@media (max-width: 768px) {
    /* 窄屏下 column-width 自动退化为单栏，只需要收紧卡片间距 */
    .ssh-card {
        margin-bottom: 12px;
    }

    .ssh-card__head {
        padding: 10px 12px;
    }

    .ssh-card__foot {
        padding: 10px 12px;
    }

    /* 移动端不依赖 hover，同时把触摸区域放大一些 */
    .ssh-cmd {
        padding: 9px 8px;
    }
}
</style>

<!-- 锁钮 tooltip 会挂到 body 下，需放在非 scoped 块里 -->
<style lang="scss">
/* 窄屏（触屏）一律不展示——手机屏幕小，浮层会盖住命令行；触摸端也没有 hover 语义 */
@media (max-width: 768px) {
    .ssh-lock-tip {
        display: none;
    }
}
</style>
